import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Download as DownloadIcon, Edit as EditIcon, Refresh as RefreshIcon, Tune as TuneIcon } from '@mui/icons-material';
import apiService from '../../services/api';

// Shared by the Internet and Mobile financial reports (admin only). Everything that differs
// between them lives in CONFIG below.

export type FinancialCategory = 'internet' | 'mobile';

// ---------------------------------------------------------------------------- types

interface ReportRow {
  id: string;
  service_id: string;
  service_type: string;
  status: string;
  account_id: string;
  account_name: string | null;
  account_number: string | null;
  plan_id: string | null;
  plan_name: string | null;
  speed_plan: string | null;
  technology_type: string | null;
  pop: string | null;
  carrier: string | null;
  retail_inc_gst: number | null;
  retail: number | null;
  cost: number | null;
  margin: number | null;
  margin_pct: number | null;
  price_source: 'plan' | 'override' | null;
  cost_source: 'plan' | 'override' | null;
  missing_price: boolean;
  missing_cost: boolean;
  has_override: boolean;
  override_note: string | null;
  static_ip: boolean;
}

interface Totals {
  services: number;
  priced: number;
  unpriced: number;
  costed: number;
  uncosted: number;
  retail_total: number;
  cost_total: number;
  margin_total: number;
  margin_pct: number | null;
  margin_services: number;
  margin_basis_retail: number;
}

interface Group {
  service_type: string;
  label: string;
  rows: ReportRow[];
  totals: Totals;
}

interface FacetOption {
  value: string;
  label: string;
  count: number;
}

interface Facet {
  options: FacetOption[];
  none_count: number;
}

type FacetKey = 'plan' | 'technology' | 'pop' | 'carrier';

interface Report {
  groups: Group[];
  totals: Totals;
  /** Dropdown options per filter, narrowed by the other filters' current selections */
  facets: Partial<Record<FacetKey, Facet>>;
  gst_note: string;
}

const NO_VALUE = '__none__'; // filter value for "services with none of this"

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'all', label: 'All' },
];

// ----------------------------------------------------------------------------- config

interface FacetDef {
  key: FacetKey;
  label: string;
  allLabel: string;
  noneLabel: string;
  minWidth: number;
}

const FACETS: Record<FacetKey, FacetDef> = {
  plan: { key: 'plan', label: 'Plan', allLabel: 'All plans', noneLabel: 'No plan found', minWidth: 230 },
  technology: { key: 'technology', label: 'Technology', allLabel: 'All technologies', noneLabel: 'None / not applicable', minWidth: 190 },
  pop: { key: 'pop', label: 'POP / POI', allLabel: 'All POP / POI', noneLabel: 'No POP / POI', minWidth: 240 },
  carrier: { key: 'carrier', label: 'Carrier', allLabel: 'All carriers', noneLabel: 'No carrier', minWidth: 200 },
};

interface Config {
  title: string;
  noun: string;
  typeOptions: { value: string; label: string }[] | null; // null = a single service type, no selector
  facets: FacetKey[];
  showTechnology: boolean;
  showPop: boolean;
  showCarrier: boolean;
  totalLabel: string;
  canSetPlan: boolean; // plan lives on the service and can be assigned from the report (mobile)
  noPriceHint: string;
}

const CONFIG: Record<FinancialCategory, Config> = {
  internet: {
    title: 'Internet Financial Report',
    noun: 'Internet service',
    typeOptions: [
      { value: 'all', label: 'All Internet' },
      { value: 'nbn', label: 'NBN Co' },
      { value: 'supa_networks', label: 'Supa Networks' },
    ],
    facets: ['plan', 'technology', 'pop'],
    showTechnology: true,
    showPop: true,
    showCarrier: false,
    totalLabel: 'Total (all Internet)',
    canSetPlan: false,
    noPriceHint: 'no plan found and no override',
  },
  mobile: {
    title: 'Mobile Financial Report',
    noun: 'mobile service',
    typeOptions: null,
    facets: ['plan', 'carrier'],
    showTechnology: false,
    showPop: false,
    showCarrier: true,
    totalLabel: 'Total',
    canSetPlan: true,
    noPriceHint: 'no plan set and no override; use "Set plan" on the row',
  },
};

// ------------------------------------------------------------------------- helpers

const currency = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' });
const fmtMoney = (n: number | null | undefined) => (n == null ? '—' : currency.format(n));
const fmtPct = (n: number | null | undefined) => (n == null ? '—' : `${n.toFixed(2)}%`);
const marginColor = (n: number | null | undefined) => (n == null ? 'text.secondary' : n < 0 ? 'error.main' : 'text.primary');

// Spreadsheet apps run text starting with = + - @ as a formula; neutralise user-supplied text
const csvText = (v: string | null | undefined) => {
  const s = (v ?? '').toString();
  const safe = /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
  return `"${safe.replace(/"/g, '""')}"`;
};
const csvNum = (n: number | null | undefined) => (n == null ? '' : n.toFixed(2));

function rowNotes(r: ReportRow): string {
  const notes: string[] = [];
  if (r.missing_price) notes.push('No price');
  if (r.missing_cost) notes.push('No cost');
  if (r.has_override) notes.push(`Override${r.override_note ? `: ${r.override_note}` : ''}`);
  if (r.static_ip) notes.push('Static IP');
  return notes.join('; ');
}

function buildCsv(report: Report, groupByType: boolean, cfg: Config): string {
  const extras: { header: string; value: (r: ReportRow) => string | null }[] = [];
  if (cfg.showTechnology) extras.push({ header: 'Technology', value: (r) => r.technology_type });
  if (cfg.showPop) extras.push({ header: 'POP / POI', value: (r) => r.pop });
  if (cfg.showCarrier) extras.push({ header: 'Carrier', value: (r) => r.carrier });
  const header = [
    'Group', 'Service ID', 'Account', 'Account number', 'Plan', ...extras.map((e) => e.header), 'Status',
    'Retail ex GST', 'Cost ex GST', 'Margin $', 'Margin %', 'Notes',
  ];
  const lines = [header.map((h) => `"${h}"`).join(',')];
  const blanks = (n: number) => Array(n).fill('');
  const totalLine = (label: string, t: Totals) =>
    [csvText(label), ...blanks(5 + extras.length), csvNum(t.retail_total), csvNum(t.cost_total), csvNum(t.margin_total), csvNum(t.margin_pct), ''].join(',');
  for (const g of report.groups) {
    for (const r of g.rows) {
      lines.push([
        csvText(g.label), csvText(r.service_id), csvText(r.account_name), csvText(r.account_number), csvText(r.plan_name),
        ...extras.map((e) => csvText(e.value(r))), csvText(r.status),
        csvNum(r.retail), csvNum(r.cost), csvNum(r.margin), csvNum(r.margin_pct), csvText(rowNotes(r)),
      ].join(','));
    }
    if (groupByType) lines.push(totalLine(`${g.label} subtotal`, g.totals));
  }
  lines.push(totalLine('TOTAL', report.totals));
  return lines.join('\r\n');
}

function downloadCsv(filename: string, content: string) {
  const blob = new Blob([`﻿${content}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const apiMessage = (e: any, fallback: string) => {
  const detail = e?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  return fallback;
};

const MONEY_PATTERN = /^\d{1,5}(\.\d{1,2})?$/;

// --------------------------------------------------------------------- override dialog

const OverrideDialog: React.FC<{ category: FinancialCategory; row: ReportRow; onClose: (changed: boolean) => void }> = ({ category, row, onClose }) => {
  const [price, setPrice] = useState('');
  const [cost, setCost] = useState('');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiService
      .getServicePricingOverride(category, row.id)
      .then((o) => {
        setPrice(o.monthly_price != null ? o.monthly_price.toFixed(2) : '');
        setCost(o.monthly_cost != null ? o.monthly_cost.toFixed(2) : '');
        setNote(o.note || '');
      })
      .catch((e) => setError(apiMessage(e, 'Could not load the override.')))
      .finally(() => setLoading(false));
  }, [category, row.id]);

  const save = async () => {
    setError(null);
    if (price.trim() && !MONEY_PATTERN.test(price.trim())) return setError('Retail price must be a dollar amount, e.g. 84.00');
    if (cost.trim() && !MONEY_PATTERN.test(cost.trim())) return setError('Cost must be a dollar amount, e.g. 60.00');
    if (!price.trim() && !cost.trim()) return setError('Enter a retail price, a cost, or both (or remove the override).');
    setBusy(true);
    try {
      await apiService.setServicePricingOverride(category, row.id, {
        monthly_price: price.trim() || null,
        monthly_cost: cost.trim() || null,
        note: note.trim() || null,
      });
      onClose(true);
    } catch (e) {
      setError(apiMessage(e, 'Could not save the override.'));
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await apiService.deleteServicePricingOverride(category, row.id);
      onClose(true);
    } catch (e) {
      setError(apiMessage(e, 'Could not remove the override.'));
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={() => !busy && onClose(false)} maxWidth="sm" fullWidth>
      <DialogTitle>Pricing override: {row.service_id}</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Plan: {row.plan_name || 'none found'}. An override replaces the plan price and cost
              {category === 'internet' ? ' (including any Static IP add-on)' : ''} for this service only. Leave a field blank to keep the plan figure.
            </Typography>
            <TextField
              label="Monthly retail price (inc GST)"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              helperText="What the customer is billed per month, including GST."
              disabled={busy}
            />
            <TextField
              label="Monthly cost (ex GST)"
              value={cost}
              onChange={(e) => setCost(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              helperText="Your wholesale cost per month, excluding GST."
              disabled={busy}
            />
            <TextField
              label="Reason (optional)"
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, 500))}
              placeholder="e.g. grandfathered pricing, negotiated rate"
              disabled={busy}
            />
            {error && <Alert severity="error">{error}</Alert>}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {row.has_override && (
          <Button color="error" onClick={remove} disabled={busy || loading} sx={{ mr: 'auto' }}>
            Remove override
          </Button>
        )}
        <Button onClick={() => onClose(false)} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={busy || loading}>
          {busy ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ------------------------------------------------------------------- set-plan dialog (mobile)

interface PlanCost {
  product_id: string;
  name: string;
  service_type: string | null;
  is_add_on: boolean;
  is_active: boolean;
  retail_inc_gst: number | null;
  retail: number | null;
  monthly_cost: number | null;
}

const SetPlanDialog: React.FC<{ row: ReportRow; onClose: (changed: boolean) => void }> = ({ row, onClose }) => {
  const [plans, setPlans] = useState<PlanCost[]>([]);
  const [planId, setPlanId] = useState(row.plan_id || '');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiService
      .getFinancialPlanCosts('mobile')
      .then(setPlans)
      .catch((e) => setError(apiMessage(e, 'Could not load the mobile plans.')))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await apiService.setMobileServicePlan(row.id, planId || null);
      onClose(true);
    } catch (e) {
      setError(apiMessage(e, 'Could not set the plan.'));
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={() => !busy && onClose(false)} maxWidth="sm" fullWidth>
      <DialogTitle>Mobile plan: {row.service_id}</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Sets which plan this service is on. This is the same field as <em>Mobile plan</em> on the service itself, so everyone sees it there too.
            </Typography>
            <TextField select label="Plan" value={planId} onChange={(e) => setPlanId(e.target.value)} disabled={busy}>
              <MenuItem value="">(No plan)</MenuItem>
              {plans.map((p) => (
                <MenuItem key={p.product_id} value={p.product_id}>
                  {p.name} ({fmtMoney(p.retail_inc_gst)} inc GST)
                </MenuItem>
              ))}
            </TextField>
            {error && <Alert severity="error">{error}</Alert>}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={() => onClose(false)} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={busy || loading || planId === (row.plan_id || '')}>
          {busy ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// -------------------------------------------------------------------- plan costs dialog

const PlanCostsDialog: React.FC<{ category: FinancialCategory; onClose: (changed: boolean) => void }> = ({ category, onClose }) => {
  const [plans, setPlans] = useState<PlanCost[]>([]);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [changed, setChanged] = useState(false);

  const load = useCallback(async () => {
    try {
      const rows: PlanCost[] = await apiService.getFinancialPlanCosts(category);
      setPlans(rows);
      setDrafts(Object.fromEntries(rows.map((p) => [p.product_id, p.monthly_cost != null ? p.monthly_cost.toFixed(2) : ''])));
    } catch (e) {
      setError(apiMessage(e, 'Could not load plans.'));
    } finally {
      setLoading(false);
    }
  }, [category]);
  useEffect(() => { load(); }, [load]);

  const save = async (p: PlanCost) => {
    const value = (drafts[p.product_id] || '').trim();
    if (value && !MONEY_PATTERN.test(value)) return setError(`${p.name}: enter a dollar amount, e.g. 60.00`);
    setError(null);
    setSavingId(p.product_id);
    try {
      await apiService.setProductCost(p.product_id, value || null);
      setChanged(true);
      await load();
    } catch (e) {
      setError(apiMessage(e, `Could not save the cost for ${p.name}.`));
    } finally {
      setSavingId(null);
    }
  };

  return (
    <Dialog open onClose={() => onClose(changed)} maxWidth="md" fullWidth>
      <DialogTitle>Plan costs</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Monthly cost per plan, <strong>ex GST</strong>. Every service on a plan uses its cost.
          {category === 'internet' ? ' The Static IP add-on cost is optional (left blank it adds nothing to cost).' : ''}
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Plan</TableCell>
                  <TableCell>Network</TableCell>
                  <TableCell align="right">Retail (inc GST)</TableCell>
                  <TableCell align="right">Retail (ex GST)</TableCell>
                  <TableCell align="right" sx={{ width: 170 }}>Monthly cost (ex GST)</TableCell>
                  <TableCell sx={{ width: 90 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {plans.map((p) => {
                  const dirty = (drafts[p.product_id] || '') !== (p.monthly_cost != null ? p.monthly_cost.toFixed(2) : '');
                  return (
                    <TableRow key={p.product_id} sx={{ opacity: p.is_active ? 1 : 0.6 }}>
                      <TableCell>
                        {p.name} {!p.is_active && <Chip size="small" label="Inactive" sx={{ ml: 0.5 }} />}
                        {p.is_add_on && <Chip size="small" label="Add-on" variant="outlined" sx={{ ml: 0.5 }} />}
                      </TableCell>
                      <TableCell>{p.service_type || '—'}</TableCell>
                      <TableCell align="right">{fmtMoney(p.retail_inc_gst)}</TableCell>
                      <TableCell align="right">{fmtMoney(p.retail)}</TableCell>
                      <TableCell align="right">
                        <TextField
                          size="small"
                          value={drafts[p.product_id] ?? ''}
                          onChange={(e) => setDrafts((d) => ({ ...d, [p.product_id]: e.target.value }))}
                          InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                          inputProps={{ 'aria-label': `Monthly cost for ${p.name}`, style: { textAlign: 'right' } }}
                          disabled={savingId === p.product_id}
                        />
                      </TableCell>
                      <TableCell>
                        <Button size="small" variant={dirty ? 'contained' : 'text'} disabled={!dirty || savingId === p.product_id} onClick={() => save(p)}>
                          {savingId === p.product_id ? '…' : 'Save'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="contained" onClick={() => onClose(changed)}>Done</Button>
      </DialogActions>
    </Dialog>
  );
};

// --------------------------------------------------------------------------- the page

const SummaryTile: React.FC<{ label: string; value: string; sub?: string; color?: string }> = ({ label, value, sub, color }) => (
  <Paper variant="outlined" sx={{ p: 2, flex: '1 1 170px', minWidth: 170 }}>
    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
      {label}
    </Typography>
    <Typography variant="h5" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: color || 'text.primary' }}>{value}</Typography>
    {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
  </Paper>
);

const numCell = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } as const;

const FinancialReportPage: React.FC<{ category: FinancialCategory }> = ({ category }) => {
  const cfg = CONFIG[category];
  const [serviceType, setServiceType] = useState('all');
  const [status, setStatus] = useState('active');
  const [selected, setSelected] = useState<Partial<Record<FacetKey, string>>>({});
  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [overrideRow, setOverrideRow] = useState<ReportRow | null>(null);
  const [planRow, setPlanRow] = useState<ReportRow | null>(null);
  const [planCostsOpen, setPlanCostsOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data: Report = await apiService.getFinancialReport(category, {
        ...(cfg.typeOptions ? { service_type: serviceType } : {}),
        status,
        ...selected,
      });
      setReport(data);
      // A selection that no longer exists under the other filters would give an empty report: reset it
      const stale = cfg.facets.filter((k) => {
        const v = selected[k];
        const f = data.facets[k];
        return !!v && !!f && !(v === NO_VALUE ? f.none_count > 0 : f.options.some((o) => o.value === v));
      });
      if (stale.length) setSelected((cur) => Object.fromEntries(Object.entries(cur).filter(([k]) => !stale.includes(k as FacetKey))));
    } catch (e) {
      setError(apiMessage(e, 'Could not load the report.'));
    } finally {
      setLoading(false);
    }
  }, [category, cfg, serviceType, status, selected]);

  useEffect(() => { load(); }, [load]);

  const groupByType = !!cfg.typeOptions && serviceType === 'all';
  const t = report?.totals;
  const filtersActive = cfg.facets.some((k) => !!selected[k]);
  const leadCols = 3 + (cfg.showTechnology ? 1 : 0) + (cfg.showPop ? 1 : 0) + (cfg.showCarrier ? 1 : 0) + 1; // service, account, plan, extras, status
  const totalCols = leadCols + 5;

  const exportCsv = () => {
    if (!report) return;
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`${category}-financial-${serviceType}-${status}${filtersActive ? '-filtered' : ''}-${stamp}.csv`, buildCsv(report, groupByType, cfg));
  };

  const renderRow = (r: ReportRow) => (
    <TableRow key={r.id} hover>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.service_id}</TableCell>
      <TableCell>
        <Box component={RouterLink} to={`/accounts/${r.account_id}`} sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
          {r.account_name || '—'}
        </Box>
        {r.account_number && <Typography variant="caption" color="text.secondary" display="block">{r.account_number}</Typography>}
      </TableCell>
      <TableCell>
        {r.plan_name || (r.speed_plan ? <Typography variant="body2" color="text.secondary">{r.speed_plan}</Typography> : '—')}
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: r.has_override || r.static_ip || cfg.canSetPlan ? 0.5 : 0 }}>
          {r.static_ip && <Chip size="small" label="Static IP" variant="outlined" />}
          {r.has_override && (
            <Tooltip title={r.override_note || 'Price and/or cost overridden for this service'}>
              <Chip size="small" color="info" label="Override" />
            </Tooltip>
          )}
          {cfg.canSetPlan && (
            <Chip
              size="small"
              variant="outlined"
              clickable
              color={r.plan_id ? 'default' : 'warning'}
              label={r.plan_id ? 'Change plan' : 'Set plan'}
              onClick={() => setPlanRow(r)}
            />
          )}
        </Box>
      </TableCell>
      {cfg.showTechnology && <TableCell>{r.technology_type || '—'}</TableCell>}
      {cfg.showPop && <TableCell>{r.pop || '—'}</TableCell>}
      {cfg.showCarrier && <TableCell>{r.carrier || '—'}</TableCell>}
      <TableCell>
        <Chip size="small" label={r.status} color={r.status === 'active' ? 'success' : 'default'} variant="outlined" sx={{ textTransform: 'capitalize' }} />
      </TableCell>
      <TableCell align="right" sx={numCell}>
        {r.missing_price ? <Chip size="small" color="warning" label="No price" /> : fmtMoney(r.retail)}
      </TableCell>
      <TableCell align="right" sx={numCell}>
        {r.missing_cost ? <Chip size="small" color="warning" label="No cost" /> : fmtMoney(r.cost)}
      </TableCell>
      <TableCell align="right" sx={{ ...numCell, color: marginColor(r.margin) }}>{fmtMoney(r.margin)}</TableCell>
      <TableCell align="right" sx={{ ...numCell, color: marginColor(r.margin_pct) }}>{fmtPct(r.margin_pct)}</TableCell>
      <TableCell align="right" padding="checkbox">
        <Tooltip title="Set a pricing override for this service">
          <IconButton size="small" onClick={() => setOverrideRow(r)} aria-label={`Edit pricing for ${r.service_id}`}><EditIcon fontSize="small" /></IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );

  const totalRow = (label: string, tt: Totals, key: string, strong = true) => (
    <TableRow key={key} sx={{ bgcolor: 'action.hover' }}>
      <TableCell colSpan={leadCols} sx={{ fontWeight: strong ? 700 : 600 }}>
        {label} <Typography component="span" variant="caption" color="text.secondary">({tt.services} service{tt.services !== 1 ? 's' : ''})</Typography>
      </TableCell>
      <TableCell align="right" sx={{ ...numCell, fontWeight: 700 }}>{fmtMoney(tt.retail_total)}</TableCell>
      <TableCell align="right" sx={{ ...numCell, fontWeight: 700 }}>{fmtMoney(tt.cost_total)}</TableCell>
      <TableCell align="right" sx={{ ...numCell, fontWeight: 700, color: marginColor(tt.margin_total) }}>{fmtMoney(tt.margin_total)}</TableCell>
      <TableCell align="right" sx={{ ...numCell, fontWeight: 700, color: marginColor(tt.margin_pct) }}>{fmtPct(tt.margin_pct)}</TableCell>
      <TableCell />
    </TableRow>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>{cfg.title}</Typography>
          <Typography variant="body2" color="text.secondary">
            Monthly retail, cost and margin for each {cfg.noun}. {report?.gst_note || 'All figures ex GST.'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="outlined" startIcon={<TuneIcon />} onClick={() => setPlanCostsOpen(true)}>Plan costs</Button>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={exportCsv} disabled={!report || loading}>Export CSV</Button>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Refresh</Button>
        </Box>
      </Box>

      <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {cfg.typeOptions && (
          <TextField select size="small" label="Service type" value={serviceType} onChange={(e) => setServiceType(e.target.value)} sx={{ minWidth: 180 }}>
            {cfg.typeOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </TextField>
        )}
        <TextField select size="small" label="Status" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ minWidth: 140 }}>
          {STATUS_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </TextField>
        {cfg.facets.map((key) => {
          const def = FACETS[key];
          const facet = report?.facets[key];
          const value = selected[key] || '';
          return (
            <TextField
              key={key}
              select
              size="small"
              label={def.label}
              value={value}
              onChange={(e) => setSelected((cur) => ({ ...cur, [key]: e.target.value }))}
              sx={{ minWidth: def.minWidth }}
            >
              <MenuItem value="">{def.allLabel}</MenuItem>
              {(facet?.options || []).map((o) => <MenuItem key={o.value} value={o.value}>{o.label} ({o.count})</MenuItem>)}
              {((facet?.none_count ?? 0) > 0 || value === NO_VALUE) && (
                <MenuItem value={NO_VALUE}>{key === 'plan' && category === 'mobile' ? 'No plan set' : def.noneLabel} ({facet?.none_count ?? 0})</MenuItem>
              )}
            </TextField>
          );
        })}
        {filtersActive && (
          <Button size="small" onClick={() => setSelected({})} sx={{ alignSelf: 'center' }}>
            Clear filters
          </Button>
        )}
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {t && (
        <>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <SummaryTile label="Services" value={t.services.toLocaleString()} />
            <SummaryTile label="Monthly retail" value={fmtMoney(t.retail_total)} sub="ex GST" />
            <SummaryTile label="Monthly cost" value={fmtMoney(t.cost_total)} sub="ex GST" />
            <SummaryTile label="Margin" value={fmtMoney(t.margin_total)} color={marginColor(t.margin_total) as string} />
            <SummaryTile label="Margin %" value={fmtPct(t.margin_pct)} color={marginColor(t.margin_pct) as string} />
          </Box>
          {(t.unpriced > 0 || t.uncosted > 0) && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t.unpriced > 0 && <div><strong>{t.unpriced}</strong> service{t.unpriced !== 1 ? 's have' : ' has'} no price ({cfg.noPriceHint}) and {t.unpriced !== 1 ? 'are' : 'is'} not in the retail total.</div>}
              {t.uncosted > 0 && (
                <div>
                  <strong>{t.uncosted}</strong> service{t.uncosted !== 1 ? 's have' : ' has'} no cost, so margin is calculated on the {t.margin_services} service{t.margin_services !== 1 ? 's' : ''} that
                  {t.margin_services !== 1 ? ' have' : ' has'} both a price and a cost. Use <em>Plan costs</em> to enter missing costs.
                </div>
              )}
            </Alert>
          )}
        </>
      )}

      <Paper variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Service</TableCell>
                <TableCell>Account</TableCell>
                <TableCell>Plan</TableCell>
                {cfg.showTechnology && <TableCell>Technology</TableCell>}
                {cfg.showPop && <TableCell>POP / POI</TableCell>}
                {cfg.showCarrier && <TableCell>Carrier</TableCell>}
                <TableCell>Status</TableCell>
                <TableCell align="right">Retail / mth</TableCell>
                <TableCell align="right">Cost / mth</TableCell>
                <TableCell align="right">Margin $</TableCell>
                <TableCell align="right">Margin %</TableCell>
                <TableCell padding="checkbox" />
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && !report && (
                <TableRow><TableCell colSpan={totalCols} align="center" sx={{ py: 6 }}><CircularProgress size={28} /></TableCell></TableRow>
              )}
              {report && t && t.services === 0 && !loading && (
                <TableRow><TableCell colSpan={totalCols} align="center" sx={{ py: 6, color: 'text.secondary' }}>No services match these filters.</TableCell></TableRow>
              )}
              {report && report.groups.map((g) => (
                <React.Fragment key={g.service_type}>
                  {groupByType && g.rows.length > 0 && (
                    <TableRow>
                      <TableCell colSpan={totalCols} sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', fontWeight: 700 }}>{g.label}</TableCell>
                    </TableRow>
                  )}
                  {g.rows.map(renderRow)}
                  {groupByType && g.rows.length > 0 && totalRow(`${g.label} subtotal`, g.totals, `sub-${g.service_type}`, false)}
                </React.Fragment>
              ))}
              {report && t && t.services > 0 && totalRow(groupByType ? cfg.totalLabel : 'Total', t, 'grand')}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {overrideRow && (
        <OverrideDialog category={category} row={overrideRow} onClose={(changed) => { setOverrideRow(null); if (changed) load(); }} />
      )}
      {planRow && (
        <SetPlanDialog row={planRow} onClose={(changed) => { setPlanRow(null); if (changed) load(); }} />
      )}
      {planCostsOpen && (
        <PlanCostsDialog category={category} onClose={(changed) => { setPlanCostsOpen(false); if (changed) load(); }} />
      )}
    </Box>
  );
};

export default FinancialReportPage;
